import { Modal, message } from "antd";
import { useEffect, useState } from "react";
import Cropper from "react-easy-crop";

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = document.createElement("img");
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("이미지를 불러오지 못했습니다."));
    img.src = src;
  });

const createCroppedImageFile = async ({
  imageSrc,
  croppedAreaPixels,
  originalFile,
  width,
  height,
}) => {
  const image = await loadImage(imageSrc);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("이미지 변환에 실패했습니다.");
  }

  ctx.drawImage(
    image,
    croppedAreaPixels.x,
    croppedAreaPixels.y,
    croppedAreaPixels.width,
    croppedAreaPixels.height,
    0,
    0,
    width,
    height
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("이미지 변환에 실패했습니다."));
          return;
        }

        const baseName =
          originalFile.name.replace(/\.[^.]+$/, "") || "cropped-image";

        resolve(
          new File([blob], `${baseName}.jpg`, {
            type: "image/jpeg",
          })
        );
      },
      "image/jpeg",
      0.92
    );
  });
};

export const ImageCropper = ({
  open,
  imageUrl,
  originalFile,
  aspect,
  outputWidth,
  outputHeight,
  title = "이미지 자르기",
  helperText = "업로드한 이미지를 원하는 비율에 맞게 잘라주세요.",
  onCancel,
  onConfirm,
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  useEffect(() => {
    if (!open) {
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
    }
  }, [open, imageUrl]);

  const handleConfirm = async () => {
    if (!imageUrl || !originalFile || !croppedAreaPixels) {
      message.warning("자를 영역을 확인해주세요.");
      return;
    }

    try {
      const croppedFile = await createCroppedImageFile({
        imageSrc: imageUrl,
        croppedAreaPixels,
        originalFile,
        width: outputWidth,
        height: outputHeight,
      });
      onConfirm?.(croppedFile);
    } catch (error) {
      message.error(error?.message || "이미지 변환에 실패했습니다.");
    }
  };

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onCancel}
      onOk={handleConfirm}
      okText="적용"
      cancelText="취소"
      width={760}
      destroyOnHidden
    >
      <div className="space-y-4">
        <p className="text-[14px] text-gray-500">{helperText}</p>
        <div className="relative h-[420px] overflow-hidden rounded-2xl bg-[#111827]">
          {imageUrl ? (
            <Cropper
              image={imageUrl}
              crop={crop}
              zoom={zoom}
              aspect={aspect}
              onCropChange={setCrop}
              onCropComplete={(_, pixels) => setCroppedAreaPixels(pixels)}
              onZoomChange={setZoom}
            />
          ) : null}
        </div>
        <div className="flex items-center gap-4">
          <span className="min-w-fit text-[14px] font-medium text-gray-700">
            확대
          </span>
          <input
            type="range"
            min={1}
            max={3}
            step={0.01}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="w-full"
          />
        </div>
      </div>
    </Modal>
  );
};
